import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Mock user data - In production, this would come from your backend
const mockUsers = [
  {
    id: 1,
    username: 'admin',
    password: 'admin123',
    role: 'admin',
    name: 'Admin User',
    s3Folder: 'admin',
    permissions: ['read', 'write', 'delete', 'admin']
  },
  {
    id: 2,
    username: 'client1',
    password: 'client123',
    role: 'client',
    name: 'John Doe',
    s3Folder: 'client1-uploads',
    permissions: ['read', 'write']
  },
  {
    id: 3,
    username: 'client2',
    password: 'client456',
    role: 'client',
    name: 'Jane Smith',
    s3Folder: 'client2-uploads',
    permissions: ['read', 'write']
  }
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState(mockUsers);

  useEffect(() => {
    // Check for saved session
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    const foundUser = users.find(u => u.username === username && u.password === password);
    if (foundUser) {
      const { password: _, ...userWithoutPassword } = foundUser;
      setUser(userWithoutPassword);
      localStorage.setItem('user', JSON.stringify(userWithoutPassword));
      return { success: true };
    }
    return { success: false, error: 'Invalid credentials' };
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const createUser = (userData) => {
    const newUser = {
      ...userData,
      id: Date.now(),
      role: 'client',
      permissions: ['read', 'write']
    };
    setUsers(prev => [...prev, newUser]);
    return newUser;
  };

  const updateUser = (userId, updates) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updates } : u));
  };

  const deleteUser = (userId) => {
    setUsers(prev => prev.filter(u => u.id !== userId));
  };

  const value = {
    user,
    users: users.filter(u => u.role === 'client'), // Only expose clients to admin
    loading,
    login,
    logout,
    createUser,
    updateUser,
    deleteUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}